/**
 * Ermittelt alle Kommentar-Elemente innerhalb des root Elements
 *
 * @param {HTMLElement} root
 */
export const getElementComments = function (root) {
  // https://jsperf.com/getcomments/6
  const treeWalker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_COMMENT,
    {
      "acceptNode": function acceptNode(node) {
        return NodeFilter.FILTER_ACCEPT;
      }
    },
    false
  );

  // skip the first node which is the node specified in the `root`
  treeWalker.nextNode();
  const nodeList = [];
  while (treeWalker.nextNode()) {
    nodeList.push(treeWalker.currentNode);
  }
  return nodeList;
};

export function onTransitionEnd(element, timeout = null) {
  // resove wenn fertig ein-/ausgeblendet
  return new Promise((resolve) => {
    const styles = window.getComputedStyle(element, null);
    if (styles.transitionDuration != "0s" || styles.transitionDelay != "0s") {
      element.addEventListener("transitionend", resolve, {once: true});
    } else {
      setTimeout(resolve, 1);
    }
    if (timeout !== null) {
      setTimeout(resolve, timeout);
    }
  });
}

export function promseTimeout(handler, timeout, ...args) {
  return new Promise(function (resolve) {
    setTimeout(resolve, timeout);
  }).then(() => handler(...args));
}

export function elementFromString(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.children[0];
}

/**
 * Nur für responsive-Layout verfügbar
 * @returns {string} ['', 'xs', 'md', 'xl']
 */
export function getViewport() {
  const elem = document.getElementById('layout-viewport');
  let viewport = '';
  if (elem) {
    viewport = (window.getComputedStyle(elem, ':before').content || viewport).replace(/[^\w]/g, '');
  }
  return viewport.toLowerCase();
}

/** Gibt zurück ob der gefrage Viewport aktiv ist. Dabei gilt mobile first:
 * isViewport('xs') wird immer true sein
 * isViewport('md') ist true wenn der Viewport xs oder md ist.
 * Gibt true zurück wenn der aktuelle Viewport unbekannt ist.
 * @return {boolean}
 */
export function isViewport(viewport) {
  const viewports = ['xs', 'md', 'xl'];
  const current_index = viewports.indexOf(getViewport());
  const viewport_index = viewports.indexOf(viewport);
  return (current_index === -1) || (current_index >= viewport_index);
}

/**
 * @returns {boolean}
 */
export function isResponsive() {
  return getViewport() !== '';
}

/**
 * Feuert auf dem window.element 'viewport-changed' wenn sich der viewport geändert hat.
 *
 */
export function observeViewportChange() {
  let viewport = getViewport();
  let current = viewport;
  window.addEventListener('resize', function () {
    if ((current = getViewport()) !== viewport) {
      viewport = current;
      window.dispatchEvent(new CustomEvent('viewport-changed', {detail: viewport}));
    }
  });
}

/**
 * Nur in Entwicklungsumgebung hinzufügen.
 * process.env.NODE_ENV steht nur zur Compile-Zeit zur Verfügung.
 */
if (process.env.NODE_ENV === 'development') {
  class Debug_ViewNames {
    constructor() {
      this.visible = false;
      this.pattern = /^\s*dev-viewname:/;
    }

    toggle() {
      if (this.visible) {
        if (Array.isArray(this.viewNameElements)) {
          this.viewNameElements.forEach((element) => {
            element.parentElement.removeChild(element);
          });
          this.viewNameElements = void 0;
        }
        this.visible = false;
      } else {
        this.viewNameElements = this.commentNodes.map((comment) => {
          const element = document.createElement('div');
          element.textContent = comment.nodeValue.replace(this.pattern, '');
          element.classList.add('dev-viewname', 'dev-show');
          comment.after(element);
          return element;
        });
        this.visible = true;
      }
    }

    get commentNodes() {
      return getElementComments(document.body)
        .filter((element) => {
          return element.nodeValue.match(this.pattern);
        });
    }
  }


  const viewNames = new Debug_ViewNames();
  window.tp_toggleViewNames = viewNames.toggle.bind(viewNames);
}


export const domReady = new Promise(function (resolve) {
  if (document.readyState == "loading") {
    document.addEventListener("DOMContentLoaded", resolve);
  } else {
    resolve();
  }
});

/**
 * Ermittelt die Laufzeit eines Videos
 *
 * @param {File} file
 * @returns {Promise<number>}
 */
export async function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = "metadata";
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = reject;
    video.onabort = reject;
    video.src = window.URL.createObjectURL(file);
  });
}

/**
 * Erzeugt einen Screenshot des Videos an der angegebenen Stelle
 * kann z.B. mit `img.src = window.URL.createObjectURL(blob)` benutzt werden.
 *
 * @param {File} file
 * @param {number} currentTime Zeit in Sekunden
 * @returns {Promise<Blob>} Blob enthält das JPEG-Bild
 */
export async function getVideoThumbnail(file, currentTime = 1) {
  const video = document.createElement('video');
  const onload = new Promise((resolve, reject) => {
    video.onload = resolve;
    video.onerror = reject;
  });
  video.src = window.URL.createObjectURL(file);
  video.load();
  await onload;

  const seeked = new Promise(resolve => video.onseeked = resolve);
  video.currentTime = currentTime;
  await seeked;

  const canvas = document.createElement('canvas');

  video.width = canvas.width = video.videoWidth;
  video.height = canvas.height = video.videoHeight;
  const context = canvas.getContext('2d');
  context.drawImage(video, 0, 0);

  window.URL.revokeObjectURL(video.src);

  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg'));
  return blob;
}

/**
 * Erzeugt einen HEX-String des Hashes über eine Datei
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 *
 * @param {File} file
 * @param {string} method Kompatibel mit crypto.subtle.digest
 * @returns {Promise<string>}
 */
export async function getFileHash(file, method = 'SHA-256') {
  const fileReader = new FileReader();
  const onload = new Promise(resolve => fileReader.onload = resolve);
  fileReader.readAsArrayBuffer(file);
  await onload;
  const buffer = await crypto.subtle.digest(method, fileReader.result);
  const hash = [...new Uint8Array(buffer)].map(b => b.toString(16).padStart(2, "0")).join("");
  return hash;
}

export async function getImageSize(file) {
  const img = new Image();
  const promise = new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  img.src = URL.createObjectURL(file);
  await promise;
  URL.revokeObjectURL(img.src);
  return {width: img.width, height: img.height};
}

/**
 * Skaliert ein hochzuladenes Bild kleiner wenn nötig
 *
 * @param {File} file
 * @param {object} max {width: int, height: int}
 * @param {string} type 'image/jpeg'
 * @param {int} quality 70
 * @returns {Promise<File>}
 */
export async function scaleDownImage(file, max_size = {width: 1024, height: 1024}, type = 'image/jpeg', quality = 70) {
  // Bild laden
  const image = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = URL.createObjectURL(file);
  });

  const factor = Math.min(
    max_size.width / image.width,
    max_size.height / image.height
  );
  if (factor > 1) {
    // Bild ist kleiner als erforderlich, nicht skalieren
    return file;
  }

  // Canvas initialisieren / Bild zeichnen
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(factor * image.width);
  canvas.height = Math.round(factor * image.height);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  // Bild inFile umwandeln
  const blob = await new Promise(resolve => canvas.toBlob(resolve, type, quality));
  // Speicher freigeben
  URL.revokeObjectURL(image.src);
  // in File "verpacken"
  return new File([blob], file.name, {type: type});
}

export function UserAgent() {
  let ua = {
    isAndroid: /android/i.test(navigator.userAgent),
    isIOS: /(iPhone|iPad)/i.test(navigator.userAgent),
    isMobile: null,
  }

  ua.isMobile = ua.isAndroid || ua.isIOS

  return ua
}


const utils = {
  domReady,
  elementFromString,
  getElementComments,
  isResponsive,
  onTransitionEnd,
  promseTimeout,
  UserAgent
};

export default utils;
